import * as _ from "lodash"
import {IHaveExecutionBounds} from "../PA.Core/PA.Core.IF.Aspects"
import {IMetricParameterSet, IMetrics, IMetricsSet} from "../PA.Metric.Services/PA.Metrics.IF"
import {
    IPeriodMetricContext,
    IPeriodMetrics,
    IPeriod,
    IMetricCalculator,
    IMetricCalculatorCalculationChangedEvent
} from "./PA.UIM.Metric.IF";
import {MetricParameters, MetricsParameterSet} from "../PA.Metric.Services/PAMetrics"
import {ASync} from "../PA.Core/Basic/ASync";
import {Subscription, BehaviorSubject} from 'rxjs'
import {
    WB_CustomerCalendarRow,
    WB_CustomerRow,
    WB_FinishedProductRow,
    WB_PeriodRow, WB_SourceRow
} from "../PA.Core/PAdbSchemaRows";
import {IDateRange, IProgressProvider} from "../PA.Core/PA.Core.IF.Basic";
import {DateUtils} from "../PA.Core/Basic/Dates";
import {CollectionUtils} from "../PA.Core/Basic/Collections"
import {PAMetricServiceClient} from "../PA.Metric.Services/PAMetricServiceClient";
import {ProgressProvider} from "../PA.Core/Basic/Progress";
import {SourceCustomerService} from "../PA.MD.Services/SourceCustomerService";
import {PAdbContext} from "../PA.Core/PAdbContext";
import {esWB_Customer, esWB_Customer_Calendar, esWB_Source} from "../PA.Core/PAdbSchema";
import {PeriodService} from "../PA.MD.Services/PeriodService";

//
//  Primary output
//
//    Collection of Metrics over an array
//
//
//

interface PeriodMetricsCalcRequest {
    PeriodSet: { [id: string]: IPeriod },
    PeriodMetricsParameterSet: IMetricParameterSet,
    PeriodMetricSet?: IMetricsSet
}


export class PeriodMetricContext
    implements IPeriodMetricContext {

    _loadCount: number = 0
    _calcRequestCount: number = 0
    _calcCompleteCount: number = 0

    _bCalculationInProgress: boolean = false
    _calcProgressProvider: ProgressProvider = undefined

    _executionBounds: IHaveExecutionBounds = undefined
    _ebOnChangeSubscription: Subscription = undefined
    _periodDurationInDays: number = 7
    _periodStartingDayOfWeek: number = 0
    _bufferWeekCount: number = 2
    _executionBoundsChanged: BehaviorSubject<IPeriodMetricContext>;

    _planningCustomers_All: WB_CustomerRow[] = []
    _planningCustomers_Selected: WB_CustomerRow[] = []
    _sourceCustomers_All: WB_CustomerRow[] = []
    _sourceCustomers_Selected: WB_CustomerRow[] = []
    _finishedProducts_All: WB_FinishedProductRow[] = []
    _finishedProducts_Selected: WB_FinishedProductRow[] = []
    _selectedChanged: BehaviorSubject<IPeriodMetricContext>;

    _bCalculatingPeriodsChanged: BehaviorSubject<IPeriodMetricContext>;
    _onPeriodCalculationComplete: Promise<IPeriodMetricContext> = undefined
    _calculatedPeriodsChanged: BehaviorSubject<IPeriodMetricContext>;
    _calculatedPeriods: IPeriodMetrics[] = []

    _metricCalculator: IMetricCalculator = undefined
    _metricCalculatorSub: Subscription = undefined
    _bPassBoundsProductGroups: boolean = false

    _periodType = 'Observation'
    _fiscalRange: IDateRange = undefined
    _fiscalInStoreRange: IDateRange = undefined
    _fiscalShipmentRange: IDateRange = undefined
    _observationRange: IDateRange = undefined
    _observationInStoreRange: IDateRange = undefined
    _observationShipmentRange: IDateRange = undefined
    _retailInStoreRange: IDateRange = undefined
    _retailShipmentRange: IDateRange = undefined
    _retailRange: IDateRange = undefined

    _bHasCalendar: boolean = false


    constructor(options?: { bPassExecutionBoundProductGroups?: boolean }) {
        let opts = options || {}
        this._calcProgressProvider = new ProgressProvider("Calculation Progress")
        this._calculatedPeriodsChanged = new BehaviorSubject<IPeriodMetricContext>(this)
        this._executionBoundsChanged = new BehaviorSubject<IPeriodMetricContext>(this)
        this._selectedChanged = new BehaviorSubject<IPeriodMetricContext>(this)
        this._bCalculatingPeriodsChanged = new BehaviorSubject<IPeriodMetricContext>(this)
        if (_.isBoolean(opts.bPassExecutionBoundProductGroups)) {
            this._bPassBoundsProductGroups = opts.bPassExecutionBoundProductGroups
        }
    }


    Register(anEB: IHaveExecutionBounds): void {

        let self = this
        let ConsRange: IDateRange = undefined
        let ShipRange: IDateRange = undefined
        let OuterBoundRange: IDateRange = undefined
        let aCalendar: WB_CustomerCalendarRow = undefined
        let dbContext = new PAdbContext()
        let actualWeekStart = 1

        if (_.isEmpty(anEB)) {
            this.UnRegister()
            return
        }

        this._loadCount += 1
        let internalLoadCount = this._loadCount


        let loadPromise
        if (anEB.bIHaveExecutionBoundsLoaded)
            loadPromise = anEB.OnIHaveExecutionBoundsLoaded
        else
            loadPromise = ASync.resolve(anEB)

        loadPromise
            .then((loadedEB: IHaveExecutionBounds) => {

                if (internalLoadCount != self._loadCount) {
                    // don't do anything
                    return;
                }
                self.UnRegister()

                // Setup the rest..
                self._executionBounds = loadedEB

                self._ebOnChangeSubscription
                    = loadedEB.ExecutionBoundsChanged.subscribe(self.onExecutionBoundsChanged.bind(self))

                // fired on subscribe
                // self.onExecutionBoundsChanged(loadedEB)
                ConsRange = {
                    StartDt: loadedEB.Execution_InStore.StartDt,
                    EndDt: loadedEB.Execution_InStore.EndDt
                }

                ShipRange = {
                    StartDt: loadedEB.Execution_InStore.StartDt,
                    EndDt: loadedEB.Execution_Shipment.EndDt
                }

                OuterBoundRange = DateUtils.RangeUnion(ConsRange, ShipRange)
                return dbContext.findAll(esWB_Customer_Calendar, {
                    where: {
                        DELETED_IND: {neq: '1'},
                        WB_CUST_ID: {
                            inq: _.map(self._executionBounds.Execution_PlanningCustomers, 'WB_CUST_ID')
                        }
                    },
                })
            })
            .then((arrCalendar: WB_CustomerCalendarRow[]) => {
                let rows = _.filter(arrCalendar, aRow => {
                    return DateUtils.RangesOverlap(
                        {
                            StartDt: aRow.EffectiveStartDt,
                            EndDt: aRow.EffectiveEndDt
                        },
                        OuterBoundRange)
                })
                if (rows.length) {
                    self._bHasCalendar = true
                    actualWeekStart = Number(rows[0].cif.WEEK_START_DAY_OF_WEEK_ACT)
                    return dbContext.find(esWB_Source, rows[0].cif.WB_SOURCE_ID)
                }
            }).then((aSource: WB_SourceRow) => {
            let providerStart = 1
            if (!_.isEmpty(aSource) && !_.isEmpty(aSource.cif.WEEK_START_DAY_OF_WEEK))
                providerStart = Number(aSource.cif.WEEK_START_DAY_OF_WEEK)


            let boundsStartDOW = OuterBoundRange.StartDt.getDay() + 1
            let daysToExtendStart = boundsStartDOW - providerStart
            if (daysToExtendStart < 0)
                daysToExtendStart = 7 - (-daysToExtendStart)


            self._observationRange = {
                StartDt: DateUtils.addDays(OuterBoundRange.StartDt, -1 * daysToExtendStart),
                EndDt: OuterBoundRange.EndDt
            }

            self._observationInStoreRange = {
                StartDt: DateUtils.addDays(ConsRange.StartDt, -1 * daysToExtendStart),
                EndDt: ConsRange.EndDt
            }

            self._observationShipmentRange = {
                StartDt: DateUtils.addDays(ShipRange.StartDt, -1 * daysToExtendStart),
                EndDt: ShipRange.EndDt
            }


            self._fiscalRange = self._observationRange
            self._fiscalInStoreRange = self._observationInStoreRange
            self._fiscalShipmentRange = self._observationShipmentRange


            daysToExtendStart = boundsStartDOW - actualWeekStart
            if (daysToExtendStart < 0)
                daysToExtendStart = 7 - (-daysToExtendStart)

            self._retailRange = {
                StartDt: DateUtils.addDays(OuterBoundRange.StartDt, -1 * daysToExtendStart),
                EndDt: OuterBoundRange.EndDt
            }

            self._retailInStoreRange = {
                StartDt: DateUtils.addDays(ConsRange.StartDt, -1 * daysToExtendStart),
                EndDt: ConsRange.EndDt
            }

            self._retailShipmentRange = {
                StartDt: DateUtils.addDays(ShipRange.StartDt, -1 * daysToExtendStart),
                EndDt: ShipRange.EndDt
            }

            self.queueCalc()
            return
        })
            .catch((err: any) => {
                if (internalLoadCount != self._loadCount) {
                    // don't do anything
                    return;
                }
                // Do I unregister ?
                self.UnRegister()
            })

    }

    UnRegister(): void {

        if (this._ebOnChangeSubscription) {
            this._ebOnChangeSubscription.unsubscribe()
        }
    }

    RegisterMetricCalculator(aCalculator: IMetricCalculator): void {
        const self = this

        if (_.isEmpty(aCalculator)) {
            this.UnRegisterMetricCalculator()
            return
        }

        self._metricCalculator = aCalculator
        self._metricCalculatorSub = self._metricCalculator.CalculationChanged.subscribe(self.onCalculationChanged.bind(self))

    }

    UnRegisterMetricCalculator(): void {
        if (this._metricCalculatorSub) {
            this._metricCalculatorSub.unsubscribe()
            this._metricCalculatorSub = undefined
        }

        this._metricCalculator = undefined

    }

    onCalculationChanged(event: IMetricCalculatorCalculationChangedEvent) {
        const self = this

        if (_.isEmpty(event)) {
            return
        }

        if (event.bRecalculateAll) {
            self.onExecutionBoundsChanged(self.ExecutionBounds)
        } else {

            self.updateCalculatedMetrics()
                .then((ignore) => {
                    self._calculatedPeriodsChanged.next(this)
                }).catch((err) => {
                console.log("onCalculationChanged: ", err)
            })
        }
    }

    updateCalculatedMetrics(): Promise<void> {
        const self = this
        return new Promise<void>((resolve, error) => {
            let aCalculator = self._metricCalculator

            if (_.isEmpty(aCalculator)) {
                resolve()
                return
            }

            async function CalculateAllMetrics() {
                let prds = self.CalculatedPeriods
                for (let i = 0; i < prds.length; i++) {
                    let prd = prds[i]
                    prd.CalcTotal = await aCalculator.CalculateMetrics(self, prd.Period, prd.Total)
                    prd.CalcInsideBounds = await aCalculator.CalculateMetrics(self, prd.Period, prd.InsideBounds)
                    prd.CalcOutsideBounds = await aCalculator.CalculateMetrics(self, prd.Period, prd.OutsideBounds)
                }
            }

            CalculateAllMetrics()
                .then((ignore) => {
                    resolve()
                }).catch((err) => {
                error(err)
            })

        })
    }

    onExecutionBoundsChanged(anEB: IHaveExecutionBounds) {
        const self = this

        if (_.isEmpty(anEB)) {
            return
        }


        let dbContext: PAdbContext = undefined
        if (_.isEmpty(anEB.Execution_PlanningCustomers)) {
            dbContext = new PAdbContext()
        } else {
            dbContext = <any>_.first(anEB.Execution_PlanningCustomers).dataStore()
        }

        // ReSetup drop down list...
        //- Planning Customer
        CollectionUtils.sync(self._planningCustomers_All, self._executionBounds.Execution_PlanningCustomers)
        CollectionUtils.interesect(self._planningCustomers_Selected, self._planningCustomers_All)

        //- Source Customer
        //- Add Logic to Handle the empty case
        let sourceCustomerLoadPromise = undefined
        if (_.isEmpty(self._executionBounds.Execution_SourceCustomers)) {
            let aSourceCustomerService = new SourceCustomerService();
            sourceCustomerLoadPromise = aSourceCustomerService.getSrcCustIdsbyParent(_.map(self._planningCustomers_All, (aCust) => {
                return aCust.cif.WB_CUST_ID
            }))
        } else {
            sourceCustomerLoadPromise = ASync.resolve([])
        }

        sourceCustomerLoadPromise
            .then((arrSourceCustomersIds: number[]) => {
                if (_.isEmpty(self._executionBounds.Execution_SourceCustomers)) {
                    return dbContext.findUsingCache(esWB_Customer, arrSourceCustomersIds)
                } else {
                    return ASync.resolve(self._executionBounds.Execution_SourceCustomers)
                }
            })
            .then((arrSourceCustomers: WB_CustomerRow[]) => {

                //- Source Customers
                CollectionUtils.sync(self._sourceCustomers_All, arrSourceCustomers)
                CollectionUtils.interesect(self._sourceCustomers_Selected, self._sourceCustomers_All)

                //- Finished Product
                CollectionUtils.sync(self._finishedProducts_All, self._executionBounds.Execution_FinishedProducts)
                CollectionUtils.interesect(self._finishedProducts_Selected, self._finishedProducts_All)
                self._executionBoundsChanged.next(self)
                self._selectedChanged.next(self)

                // Recalc
                self.queueCalc()

            })
            .catch((err: any) => {
                console.log(err)
            })
    }

    get CalcProgressProvider(): IProgressProvider {
        return this._calcProgressProvider
    }


    get PeriodDurationInDays(): number {
        return this._periodDurationInDays
    }


    set PeriodDurationInDays(value: number) {
        if (_.isNumber(value)) {
            this._periodDurationInDays = Math.max(Math.min(value, 60), 0)
            // this._selectedChanged.next(this)
            // this.queueCalc()
        }
    }

    get bHasCalendar(): boolean {
        return this._bHasCalendar
    }

    get PeriodType(): string {
        return this._periodType
    }

    set PeriodType(value: string) {
        this._periodType = value
        this._selectedChanged.next(this)
        this.queueCalc()
    }

    get PeriodStartingDayOfWeek(): number {
        return this._periodStartingDayOfWeek
    }

    set PeriodStartingDayOfWeek(value: number) {
        if (_.isNumber(value)) {
            this._periodStartingDayOfWeek = value % 7
            this._selectedChanged.next(this)
            this.queueCalc()
        }
    }

    get BufferWeekCount(): number {
        return this._bufferWeekCount
    }

    set BufferWeekCount(value: number) {
        this._bufferWeekCount = value
        this._selectedChanged.next(this)
        this.queueCalc()
    }

    get ExecutionBoundsChanged(): BehaviorSubject<IPeriodMetricContext> {
        return this._executionBoundsChanged
    }

    get ExecutionBounds(): IHaveExecutionBounds {
        return this._executionBounds
    }

    get PlanningCustomers_All(): WB_CustomerRow[] {
        return this._planningCustomers_All
    }


    get PlanningCustomers_Selected(): WB_CustomerRow[] {
        return this._planningCustomers_Selected
    }

    set PlanningCustomers_Selected(value: WB_CustomerRow[]) {
        CollectionUtils.sync(this._planningCustomers_Selected, value)
        this._selectedChanged.next(this)
        this.queueCalc()
    }

    get SourceCustomers_All(): WB_CustomerRow[] {
        return this._sourceCustomers_All
    }

    get SourceCustomers_Selected(): WB_CustomerRow[] {
        return this._sourceCustomers_Selected
    }

    set SourceCustomers_Selected(value: WB_CustomerRow[]) {
        CollectionUtils.sync(this._sourceCustomers_Selected, value)
        this._selectedChanged.next(this)
        this.queueCalc()
    }

    get FinishedProducts_All(): WB_FinishedProductRow[] {
        return this._finishedProducts_All
    }

    get FinishedProducts_Selected(): WB_FinishedProductRow[] {
        return this._finishedProducts_Selected
    }

    set FinishedProducts_Selected(value: WB_FinishedProductRow[]) {
        CollectionUtils.sync(this._finishedProducts_Selected, value)
        this._selectedChanged.next(this)
        this.queueCalc()
    }

    get SelectedChanged(): BehaviorSubject<IPeriodMetricContext> {
        return this._selectedChanged
    }

    get bCalculatingPeriods(): boolean {
        return (this._bCalculationInProgress)
    }


    get bCalculatingPeriodsChanged(): BehaviorSubject<IPeriodMetricContext> {
        return this._bCalculatingPeriodsChanged;
    }

    get OnPeriodCalculationComplete(): Promise<IPeriodMetricContext> {
        return this._onPeriodCalculationComplete
    }

    get CalculatedPeriodsChanged(): BehaviorSubject<IPeriodMetricContext> {
        return this._calculatedPeriodsChanged
    }

    get CalculatedPeriods(): IPeriodMetrics[] {
        return this._calculatedPeriods
    }


    private queueCalc(): void {
        const self = this

        self._calcRequestCount += 1
        if (self.bCalculatingPeriods || _.isEmpty(self._executionBounds)) {
            return;
        }
        self._bCalculationInProgress = true
        self._calcProgressProvider.start("Calculating Metrics")

        const recordedCalcRequestCount = this._calcRequestCount

        self._onPeriodCalculationComplete = new Promise<IPeriodMetricContext>((resolve, error) => {
            //console.log("Calculating Period Metrics: ", recordedCalcRequestCount)
            let apmps: any = undefined
            let aMetricServiceClient = undefined
            try {
                apmps = self.createPeriodMetricServiceParameterSet()
                aMetricServiceClient = new PAMetricServiceClient()
            } catch (err) {
                self._onPeriodCalculationComplete = undefined
                self._bCalculationInProgress = false
                self._calcProgressProvider.exception(err)
                error(err)
                return
            }

            if (_.isEmpty(apmps)) {
                self._onPeriodCalculationComplete = undefined
                self._bCalculationInProgress = false
                self._calcProgressProvider.finish()
                resolve(self)
                return
            }

            aMetricServiceClient.calculateMetricSet(apmps.PeriodMetricsParameterSet)
                .then((results: IMetricsSet) => {
                    apmps.PeriodMetricSet = results
                    self._calcProgressProvider.setPercentComplete(0.50)
                    return self.processMetricSet(apmps)
                })
                .then((ignore) => {
                    self._calcProgressProvider.setPercentComplete(0.90)
                    self._onPeriodCalculationComplete = undefined
                    self._bCalculationInProgress = false
                    self._calcProgressProvider.finish()
                    self._notifybPeriodCalculatingChanged()
                    // check if we need to recalc..
                    if (recordedCalcRequestCount < self._calcRequestCount) {
                        self.queueCalc()
                    }
                    resolve(self)
                })
                .catch((err: any) => {
                    self._onPeriodCalculationComplete = undefined
                    self._bCalculationInProgress = false
                    self._calcProgressProvider.exception(err)
                    self._notifybPeriodCalculatingChanged()
                    // check if we need to recalc..
                    if (recordedCalcRequestCount < self._calcRequestCount) {
                        self.queueCalc()
                    }
                    error(err)
                })
        })

        self._notifybPeriodCalculatingChanged()

    }

    private _notifybPeriodCalculatingChanged() {
        this._bCalculatingPeriodsChanged.next(this)
    }


    private createPeriodMetricServiceParameterSet(): PeriodMetricsCalcRequest {
        const self = this

        let eb = self._executionBounds
        if (_.isEmpty(eb)) {
            return undefined
        }
        let mps: MetricsParameterSet = new MetricsParameterSet()

        // Generate Periods..
        // Get outside bounds of shipments and consumption
        let OutsideBounds: IDateRange
        switch (self.PeriodType) {
            case  'Observation':
                OutsideBounds = self._observationRange
                break;
            case  'Fiscal':
                OutsideBounds = self._fiscalRange
                break;

            case 'Retail' :
                OutsideBounds = self._retailRange
                break;
            default:
                OutsideBounds = self._observationRange
        }

        if (_.isEmpty(OutsideBounds))
            return undefined

        let OutsideBoundsWithBuffer: IDateRange = {
            StartDt: DateUtils.addDays(OutsideBounds.StartDt, -7 * this.BufferWeekCount),
            EndDt: DateUtils.addDays(OutsideBounds.EndDt, 7 * this.BufferWeekCount)
        }

        if (!_.isDate(OutsideBoundsWithBuffer.StartDt) || !_.isDate(OutsideBoundsWithBuffer.EndDt)) {
            return undefined
        }

        /*
                let boundsStartDOW = OutsideBoundsWithBuffer.StartDt.getDay()
                let daysToExtendStart = boundsStartDOW - this.PeriodStartingDayOfWeek
                if (daysToExtendStart < 0)
                    daysToExtendStart = 7 - (-daysToExtendStart)
                OutsideBoundsWithBuffer.StartDt = DateUtils.addDays(OutsideBoundsWithBuffer.StartDt, -1 * daysToExtendStart)
          */

        let periodCount = Math.ceil(DateUtils.durationInDays(OutsideBoundsWithBuffer) / this.PeriodDurationInDays)
        let periodSet: { [id: string]: IPeriod } = {}
        let i
        for (i = 0; i < periodCount; i++) {
            //
            let periodRange = {
                StartDt: DateUtils.addDays(OutsideBoundsWithBuffer.StartDt,
                    i * this.PeriodDurationInDays),
                EndDt: DateUtils.addDays(OutsideBoundsWithBuffer.StartDt,
                    ((i + 1) * this.PeriodDurationInDays) - 1)
            }
            let iPeriod: IPeriod = {Id: i + '', Name: this.getRangeDescription(periodRange), Range: periodRange}
            periodSet[i] = iPeriod

            let mp = new MetricParameters()
            iPeriod.Params = mp

            mp.MetricParameterId = i + ''
            mp.bCalculateIntersection = true
            if (this.PlanningCustomers_Selected.length > 0) {
                mp.PlanningCustomers = _.map(this.PlanningCustomers_Selected, (pcr: WB_CustomerRow) => {
                    return pcr.cif.WB_CUST_ID
                })
            } else {
                mp.PlanningCustomers = _.map(eb.Execution_PlanningCustomers, (pcr: WB_CustomerRow) => {
                    return pcr.cif.WB_CUST_ID
                })
            }
            if (this.SourceCustomers_Selected.length > 0) {
                mp.SourceCustomers = _.map(this.SourceCustomers_Selected, (pcr: WB_CustomerRow) => {
                    return pcr.cif.WB_CUST_ID
                })
            } else {
                mp.SourceCustomers = _.map(eb.Execution_SourceCustomers, (pcr: WB_CustomerRow) => {
                    return pcr.cif.WB_CUST_ID
                })
            }
            if (this.FinishedProducts_Selected.length > 0) {
                mp.FinishedProducts = _.map(this.FinishedProducts_Selected, (fpr: WB_FinishedProductRow) => {
                    return fpr.cif.WB_PROD_ID
                })
            } else {
                mp.FinishedProducts = _.map(eb.Execution_FinishedProducts, (fpr: WB_FinishedProductRow) => {
                    return fpr.cif.WB_PROD_ID
                })

                // If there are no products available, add non value.. to prevent full dataset.
                if (_.isEmpty(mp.FinishedProducts)) {
                    mp.FinishedProducts.push(-9999)
                }
            }

            if (this._bPassBoundsProductGroups) {
                mp.ProductGroups = _.map(eb.Execution_ProductGroups, (a) => a.cif.WB_PRDGRP_ID)
            }

            mp.ConsumerProductUPCs = []
            mp.Sources = []
            mp.InStoreStartDt = periodRange.StartDt
            mp.InStoreEndDt = periodRange.EndDt
            mp.ShipmentStartDt = periodRange.StartDt
            mp.ShipmentEndDt = periodRange.EndDt

            switch (self.PeriodType) {
                case'Retail':
                    mp.Int_InStoreStartDt = self._retailInStoreRange.StartDt
                    mp.Int_InStoreEndDt = self._retailInStoreRange.EndDt
                    mp.Int_ShipmentStartDt = self._retailShipmentRange.StartDt
                    mp.Int_ShipmentEndDt = self._observationShipmentRange.EndDt
                case 'Fiscal':
                    mp.Int_InStoreStartDt = self._fiscalInStoreRange.StartDt
                    mp.Int_InStoreEndDt = self._fiscalInStoreRange.EndDt
                    mp.Int_ShipmentStartDt = self._fiscalShipmentRange.StartDt
                    mp.Int_ShipmentEndDt = self._fiscalShipmentRange.EndDt
                    break;
                default:
                    mp.Int_InStoreStartDt = self._observationInStoreRange.StartDt
                    mp.Int_InStoreEndDt = self._observationInStoreRange.EndDt
                    mp.Int_ShipmentStartDt = self._observationShipmentRange.StartDt
                    mp.Int_ShipmentEndDt = self._observationShipmentRange.EndDt
            }

            mps[mp.MetricParameterId] = mp
        }

        //  debugger, TODO: remove next line..
        //console.log("Period Metric Context Parms: ", mps)

        return {PeriodSet: periodSet, PeriodMetricsParameterSet: mps}
    }

    private getRangeDescription(aRange: IDateRange): string {

        const s_dd = aRange.StartDt.getDate()
        const s_mm = (aRange.StartDt.getMonth() + 1)
        const s_yyyy = aRange.StartDt.getFullYear()

        const e_dd = aRange.EndDt.getDate()
        const e_mm = (aRange.EndDt.getMonth() + 1)
        const e_yyyy = aRange.EndDt.getFullYear()

        if (s_yyyy == e_yyyy)
            return e_mm + '/' + e_dd + ''
        else
            return e_mm + '/' + e_dd + '/' + (e_yyyy % 100) + ''
    }

    private processMetricSet(p: PeriodMetricsCalcRequest): Promise<void> {
        const self = this

        return new Promise<void>((resolve, error) => {

            // Put results into Period Calc Records..
            if (_.isEmpty(p.PeriodMetricSet)) {
                resolve()
                return
            }

            const periodsSorted: IPeriod[] = _.sortBy(_.values(p.PeriodSet), (aPeriod: IPeriod) => {
                return aPeriod.Range.StartDt
            })

            self.CalculatedPeriods.length = 0

            _.forEach(periodsSorted, (aPeriod: IPeriod) => {
                let metrics = p.PeriodMetricSet[aPeriod.Id]
                if (!metrics) {
                    return // missing metrics for period
                }
                let outsideBoundMetrics: IMetrics = metrics.clone()
                outsideBoundMetrics.subtract(metrics.IntersectionMetrics)
                let periodMetrics: IPeriodMetrics =
                    {
                        Period: aPeriod,
                        Total: metrics,
                        InsideBounds: metrics.IntersectionMetrics,
                        OutsideBounds: outsideBoundMetrics
                    }

                self.CalculatedPeriods.push(periodMetrics)
            })

            let aCalculator = self._metricCalculator
            let beforeCall
            if (_.isEmpty(aCalculator)) {
                beforeCall = ASync.resolve()
            } else {
                beforeCall = aCalculator.BeforeCalcualteMetrics(self, p.PeriodMetricsParameterSet)
            }

            beforeCall
                .then((ignore) => {
                    return self.updateCalculatedMetrics()
                })
                .then((ignore) => {
                    self._calculatedPeriodsChanged.next(this)
                    resolve()
                })
                .catch((err) => {
                    error(err)
                })
        })

    }
}